import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { AgGridReact } from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ColGroupDef,
  type ICellRendererParams,
  type IHeaderParams,
} from 'ag-grid-community'
import {
  AlertTriangle,
  Check,
  Columns3,
  MessageCircle,
  Minus,
  Pencil,
  RotateCcw,
  Search,
  SlidersHorizontal,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import type { FilterGroup } from '@/types'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  formatPercent,
  formatUnits,
  formatWaveDate,
  PERCENT_DECIMALS,
  samePercent,
  validateWavePercent,
  distinctEvent,
  weekLabel,
} from './model'
import {
  aggregateSkuRows,
  isBalanced,
  type SkuPogStatus,
  type SkuWaveAllocation,
  type SkuWaveColumn,
  type SkuWaveLine,
  type SkuWaveRow,
} from './sku-model'
import { SkuPercentEditor } from './SkuPercentEditor'
import { SkuFilterButton } from './SkuFilterButton'
import { matchesSkuFilter } from './skuFilters'
import { focusCellControl, keepNativeKeys } from './gridEditing'
import './wave-planning.css'

ModuleRegistry.registerModules([AllCommunityModule])

const referenceColumns: {
  field: keyof SkuWaveLine
  label: string
  kind: 'text' | 'units' | 'money' | 'decimal'
  width?: number
}[] = [
  { field: 'vendor', label: 'Vendor', kind: 'text', width: 250 },
  { field: 'stores', label: 'Store Count', kind: 'units' },
  { field: 'cp', label: 'Case Pack (CP)', kind: 'units' },
  { field: 'ip', label: 'Inner Pack (IP)', kind: 'units' },
  { field: 'cube', label: 'Cube (ft³)', kind: 'decimal' },
  { field: 'cost', label: 'US Cost', kind: 'money' },
  { field: 'retail', label: 'US RTL', kind: 'money' },
  {
    field: 'childPurchaseUnits',
    label: 'Child Purchase Units',
    kind: 'units',
    width: 150,
  },
  {
    field: 'purchaseQty',
    label: 'PIAB + Single SKU Purchase Qty',
    kind: 'units',
    width: 180,
  },
  { field: 'moq', label: 'MOQ', kind: 'units' },
  { field: 'pogSet', label: 'POG Set', kind: 'units' },
  { field: 'pogMax', label: 'POG Max Qty', kind: 'units' },
  {
    field: 'totalInnerPacks',
    label: "TTL IP's",
    kind: 'units',
    width: 140,
  },
  {
    field: 'avgStoreUnits',
    label: 'AVG STR',
    kind: 'units',
    width: 140,
  },
]

const decimal = (value: number) =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
const signedUnits = (value: number) =>
  `${value > 0 ? '+' : ''}${formatUnits(value)}`
const money = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/** Narrowest SKU / Product column: room for the widest tag ("No store model"). */
const PRODUCT_MIN_WIDTH = 110

const WARNING_BADGE =
  'rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-900'

const BLOCKING_BADGE =
  'rounded bg-red-100 px-1 text-[10px] font-medium text-red-900'

/** The SKU's first wave vs its POG set (warnings by default). */
const POG_BADGES: Record<SkuPogStatus, { label: string; className: string }> = {
  short: { label: 'Below POG set', className: 'bg-amber-100 text-amber-900' },
  buyBelow: {
    label: 'Buy below POG',
    className: 'bg-amber-100 text-amber-900',
  },
  raised: { label: 'Raised for POG', className: 'bg-sky-100 text-sky-900' },
}

/**
 * The SKU and its tags. The columns showing it size their rows from its content
 * (``autoHeight``) with ``minHeight`` as the floor, so tags wrap instead of being cut
 * off at any column width.
 */
function ProductCell({
  data,
  skuOnly = false,
  minHeight,
}: ICellRendererParams<SkuWaveRow> & {
  skuOnly?: boolean
  minHeight?: number
}) {
  if (!data) return null
  if (!data.line)
    return (
      <div
        className="flex flex-col justify-center leading-tight"
        style={{ minHeight }}
      >
        <strong>Filtered totals</strong>
        {!skuOnly && (
          <p className="mt-1 text-[10px] font-normal text-muted-foreground">
            Units exclude Parent · Cube excludes Child
          </p>
        )}
      </div>
    )
  return (
    <div
      className="flex min-w-0 flex-col justify-center py-1 leading-tight"
      style={{ minHeight }}
    >
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 whitespace-normal text-[11px]">
        <span className="font-medium">{data.line.sku}</span>
        {data.adjusted && (
          <span className="text-[10px] text-primary">Adjusted</span>
        )}
        {data.needsReview && (
          <AlertTriangle
            size={12}
            className="shrink-0 text-amber-700"
            aria-label="Needs review"
          />
        )}
        {data.line.pog && (
          <span
            data-testid={`pog-badge-${data.line.pog.status}`}
            className={`rounded px-1 text-[10px] font-medium ${POG_BADGES[data.line.pog.status].className}`}
            title={data.line.pog.message}
          >
            {POG_BADGES[data.line.pog.status].label}
          </span>
        )}
        {data.line.belowMoq && (
          <span
            data-testid="moq-badge"
            className={WARNING_BADGE}
            title={data.line.belowMoq}
          >
            Below MOQ
          </span>
        )}
        {data.line.noStoreModel && (
          <span
            data-testid="no-store-model-badge"
            className={WARNING_BADGE}
            title={data.line.noStoreModel}
          >
            No store model
          </span>
        )}
        {data.line.missingInput && (
          <span
            data-testid="missing-input-badge"
            className={BLOCKING_BADGE}
            title="This line blocks submitting the plan."
          >
            {data.line.missingInput}
          </span>
        )}
      </div>
      {!skuOnly && (
        <p className="mt-1 truncate text-xs" title={data.line.description}>
          {data.line.description}
        </p>
      )}
      {!skuOnly && (
        <p className="mt-1 text-[10px] text-muted-foreground sm:hidden">
          {data.line.type} · {formatPercent(data.totalPercent ?? 0)} allocated
        </p>
      )}
    </div>
  )
}

function AllocationCell({ data }: ICellRendererParams<SkuWaveRow>) {
  if (!data || data.totalPercent === null) return '—'
  const balanced = isBalanced(data.totalPercent)
  return (
    <div
      className={`w-full text-right leading-tight ${data.needsReview ? 'text-amber-800' : ''}`}
    >
      <span className="font-medium">
        {formatPercent(data.totalPercent, balanced ? 2 : PERCENT_DECIMALS)}
      </span>
      {!balanced && (
        <p className="mt-1 text-[10px]">
          {formatPercent(Math.abs(1 - data.totalPercent), PERCENT_DECIMALS)}{' '}
          {data.totalPercent < 1 ? 'remaining' : 'over'}
        </p>
      )}
      {data.outsideWeeks.length > 0 && (
        <p className="mt-1 text-[10px]">Outside plan</p>
      )}
    </div>
  )
}

/**
 * Row selection, as in the AP's line items: shared with the checkbox cells, the
 * header and the editor through the grid context (refreshed when it changes, so
 * column definitions stay put).
 */
export interface SkuSelectionContext {
  selected: ReadonlySet<string>
  /** Ids of the rows shown (after search and filters). */
  shown: string[]
  toggle: (id: string, checked: boolean) => void
  toggleShown: (checked: boolean) => void
}

function SelectCell({
  data,
  context,
}: ICellRendererParams<SkuWaveRow, unknown, SkuSelectionContext>) {
  if (!data?.line) return null
  return (
    <Checkbox
      className="cursor-pointer"
      aria-label={`Select ${data.line.sku} ${data.line.type}`}
      checked={context.selected.has(data.id)}
      onCheckedChange={checked => context.toggle(data.id, checked)}
    />
  )
}

function SelectAllHeader({
  context,
}: IHeaderParams<SkuWaveRow, SkuSelectionContext>) {
  const shown = context.shown
  const count = shown.filter(id => context.selected.has(id)).length
  const all = shown.length > 0 && count === shown.length
  return (
    <Checkbox
      className="cursor-pointer"
      aria-label="Select the lines shown"
      checked={all}
      indeterminate={count > 0 && !all}
      onCheckedChange={context.toggleShown}
    />
  )
}

interface Props {
  rows: SkuWaveRow[]
  weeks: SkuWaveColumn[]
  commentCounts: Record<string, number>
  /** Every valid commit (the page skips edits that change nothing). */
  onEdit: (lineId: string, weekId: string, percent: string) => void
  readOnly?: boolean
  onResetRow: (lineId: string) => void
  onOpenComments: (line: SkuWaveLine, trigger: HTMLButtonElement) => void
  /** Lines can be selected (the user can edit; edits still wait for ``readOnly``). */
  canSelect?: boolean
  selected: ReadonlySet<string>
  onSelectedChange: (selected: Set<string>) => void
  /** "Reset to plan" of the selection bar. */
  onResetSelected: () => void
  bulkPending?: boolean
}

export function SkuWaveGrid({
  rows,
  weeks,
  commentCounts,
  onEdit,
  readOnly = false,
  onResetRow,
  onOpenComments,
  canSelect = !readOnly,
  selected,
  onSelectedChange,
  onResetSelected,
  bulkPending = false,
}: Props) {
  const mobile = useMediaQuery('(max-width: 639px)')
  const grid = useRef<AgGridReact<SkuWaveRow>>(null)
  const [query, setQuery] = useState('')
  const [reviewOnly, setReviewOnly] = useState(false)
  const [columnQuery, setColumnQuery] = useState('')
  const [references, setReferences] = useState<
    (typeof referenceColumns)[number]['field'][]
  >(['pogSet', 'pogMax'])
  const [separateProduct, setSeparateProduct] = useState(false)
  const [metrics, setMetrics] = useState(true)
  const [filter, setFilter] = useState<FilterGroup | null>(null)
  const filteredRefs = useMemo(() => {
    const search = columnQuery.trim().toLowerCase()
    return referenceColumns.filter(column =>
      column.label.toLowerCase().includes(search)
    )
  }, [columnQuery])
  const handleCheckboxKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    toggle: () => void
  ) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      toggle()
    }
  }
  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase()
    return rows.filter(
      row =>
        (!reviewOnly || row.needsReview) &&
        matchesSkuFilter(row, filter) &&
        (!search ||
          `${row.line?.sku} ${row.line?.description} ${row.line?.vendor}`
            .toLowerCase()
            .includes(search))
    )
  }, [rows, query, reviewOnly, filter])
  // One context object for the grid's lifetime; its fields follow the props.
  const selection = useRef<SkuSelectionContext>({
    selected,
    shown: [],
    toggle: () => {},
    toggleShown: () => {},
  })
  selection.current.selected = selected
  selection.current.shown = filtered.filter(row => row.line).map(row => row.id)
  selection.current.toggle = (id, checked) => {
    const next = new Set(selected)
    if (checked) next.add(id)
    else next.delete(id)
    onSelectedChange(next)
  }
  selection.current.toggleShown = checked => {
    const next = new Set(selected)
    for (const id of selection.current.shown)
      if (checked) next.add(id)
      else next.delete(id)
    onSelectedChange(next)
  }
  useEffect(() => {
    const api = grid.current?.api
    if (!api || api.isDestroyed()) return
    api.refreshHeader()
    api.refreshCells({ columns: ['select'], force: true })
  }, [selected, filtered])
  const totals = useMemo(
    () => [aggregateSkuRows(filtered, weeks)],
    [filtered, weeks]
  )

  const columns = useMemo<
    (ColDef<SkuWaveRow> | ColGroupDef<SkuWaveRow>)[]
  >(() => {
    // The product cell's floor: the grid's row height, less the cell's top and bottom borders.
    const productMinHeight = (mobile ? 68 : 58) - 2
    const numeric = (
      field: keyof SkuWaveRow,
      label: string,
      width: number
    ): ColDef<SkuWaveRow> => ({
      colId: field,
      headerName: label,
      initialWidth: width,
      valueGetter: params => params.data?.[field],
      cellClass: 'ag-right-aligned-cell wave-number',
      headerClass: 'ag-right-aligned-header',
      valueFormatter: params =>
        params.value == null ? '—' : formatUnits(Number(params.value)),
    })
    return [
      {
        colId: 'select',
        headerName: 'Select',
        width: 44,
        pinned: 'left',
        lockPinned: true,
        hide: !canSelect,
        sortable: false,
        resizable: false,
        headerComponent: SelectAllHeader,
        cellRenderer: SelectCell,
        cellClass: 'sku-select-cell',
        headerClass: 'sku-select-header',
        suppressKeyboardEvent: ({ event }) => keepNativeKeys(event),
      },
      {
        colId: 'product',
        headerName: 'Product',
        initialWidth: mobile ? 120 : 260,
        minWidth: PRODUCT_MIN_WIDTH,
        pinned: 'left',
        lockPinned: true,
        valueGetter: params =>
          params.data?.line
            ? `${params.data.line.sku} ${params.data.line.description}`
            : 'Filtered totals',
        cellRenderer: ProductCell,
        cellRendererParams: { minHeight: productMinHeight },
        autoHeight: true,
        cellClass: 'sku-product-cell',
        hide: separateProduct,
      },
      {
        colId: 'sku',
        headerName: 'SKU',
        initialWidth: mobile ? 120 : 165,
        minWidth: PRODUCT_MIN_WIDTH,
        pinned: 'left',
        lockPinned: true,
        hide: !separateProduct,
        valueGetter: params => params.data?.line?.sku,
        cellRenderer: ProductCell,
        cellRendererParams: { skuOnly: true, minHeight: productMinHeight },
        autoHeight: true,
        cellClass: 'sku-product-cell',
      },
      {
        colId: 'description',
        headerName: 'Description',
        initialWidth: 270,
        hide: !separateProduct,
        valueGetter: params => params.data?.line?.description,
        tooltipValueGetter: params => params.data?.line?.description,
      },
      {
        colId: 'type',
        headerName: 'Type',
        headerTooltip: 'Parent / Child / Single SKU / Replen',
        initialWidth: 102,
        hide: mobile && !separateProduct,
        valueGetter: params => params.data?.line?.type,
        cellRenderer: ({ value }: ICellRendererParams<SkuWaveRow>) =>
          value && (
            <span
              className={`rounded px-1.5 py-1 text-[11px] ${value === 'Parent' ? 'bg-accent font-medium text-primary' : 'bg-secondary text-muted-foreground'}`}
            >
              {value}
            </span>
          ),
      },
      {
        ...numeric('totalQty', 'TTL Qty', 108),
        headerTooltip: 'Assortment plan quantity the SKU schedules',
      },
      {
        ...numeric('totalPercent', 'Total %', 118),
        cellRenderer: AllocationCell,
      },
      ...referenceColumns.map(
        ({ field, label, kind, width }): ColDef<SkuWaveRow> => ({
          colId: field,
          headerName: label,
          headerTooltip: label,
          initialWidth: width ?? 115,
          hide: !references.includes(field),
          valueGetter: params => params.data?.line?.[field] ?? null,
          valueFormatter: params =>
            params.value == null
              ? '—'
              : kind === 'text'
                ? String(params.value)
                : kind === 'money'
                  ? money(Number(params.value))
                  : kind === 'decimal'
                    ? Number(params.value).toLocaleString('en-US', {
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      })
                    : formatUnits(Number(params.value)),
          cellClass:
            kind === 'text' ? undefined : 'ag-right-aligned-cell wave-number',
          tooltipValueGetter: params =>
            params.value == null ? undefined : String(params.value),
        })
      ),
      ...weeks.map((week): ColGroupDef<SkuWaveRow> => {
        const metric = (
          key: keyof SkuWaveAllocation,
          name: string,
          width: number
        ): ColDef<SkuWaveRow> => ({
          colId: `${week.id}-${key}`,
          headerName: name,
          initialWidth: width,
          valueGetter: params => params.data?.waves[week.id]?.[key],
          valueFormatter: params =>
            params.value == null
              ? '—'
              : key === 'cubicFtPerDc'
                ? decimal(Number(params.value))
                : formatUnits(Number(params.value)),
          cellClass: 'ag-right-aligned-cell wave-number',
          headerClass: 'ag-right-aligned-header',
        })
        return {
          groupId: `wave-${week.id}`,
          headerName: distinctEvent(week)
            ? `${week.event} · ${weekLabel(week.week)}`
            : week.event,
          marryChildren: true,
          headerClass: week.outside ? 'sku-outside-header' : 'sku-wave-group',
          headerGroupComponent: () => (
            <div className="flex w-full flex-col gap-1 text-xs leading-tight">
              <span className="font-semibold">
                {week.event}{' '}
                <span className="font-normal text-muted-foreground">
                  · {distinctEvent(week) && `${weekLabel(week.week)} · `}
                  {formatWaveDate(week.date)}
                </span>
              </span>
              <span
                className={
                  week.outside ? 'text-amber-800' : 'text-muted-foreground'
                }
              >
                {week.outside
                  ? 'Outside overall plan'
                  : `Plan ${formatPercent(week.overallPercent)}`}
                {week.skuPercent != null &&
                  !samePercent(
                    week.skuPercent * 100,
                    week.overallPercent * 100
                  ) && (
                    <span className="text-sky-800">
                      {' '}
                      · SKU {formatPercent(week.skuPercent)}
                    </span>
                  )}
              </span>
            </div>
          ),
          children: [
            {
              ...metric('percent', 'Wave %', 102),
              valueGetter: params => {
                const value = params.data?.waves[week.id]?.percent
                return value == null ? null : value * 100
              },
              valueFormatter: params =>
                params.value == null
                  ? '—'
                  : formatPercent(Number(params.value) / 100),
              editable: params =>
                !readOnly && !!params.data?.line && !params.node.rowPinned,
              cellClass: params =>
                `ag-right-aligned-cell wave-number ${params.node.rowPinned ? '' : 'wave-input-cell'} ${week.outside ? 'sku-outside-cell' : ''}`,
              headerComponentParams: {
                innerHeaderComponent: () => (
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <Pencil size={12} /> Wave %
                  </span>
                ),
              },
              cellEditor: SkuPercentEditor,
              cellEditorParams: {
                waveLabel: week.event,
                weekId: week.id,
                weeks,
              },
              suppressKeyboardEvent: params => {
                if (
                  params.editing &&
                  ['Enter', 'Tab'].includes(params.event.key) &&
                  params.event.target instanceof HTMLInputElement &&
                  validateWavePercent(params.event.target.value)
                ) {
                  params.event.preventDefault()
                  return true
                }
                return false
              },
              tooltipValueGetter: params =>
                `${params.data?.line?.sku ?? 'Filtered totals'} · ${week.event}: ${params.value == null ? '—' : formatPercent(Number(params.value) / 100)}${params.data?.line ? ' · Editable' : ''}`,
            },
            {
              ...metric('qty', 'Wave Qty', 108),
              // The rounded wave (what ships), so the waves add up to the Total
              // Wave Quantity; the planned units are in the tooltip.
              valueGetter: params => {
                const wave = params.data?.waves[week.id]
                return wave ? (wave.finalQty ?? wave.qty) : null
              },
              headerTooltip:
                'Units shipped in this wave: each DC share rounded up to the case pack. The waves add up to the Total Wave Quantity.',
              tooltipValueGetter: params => {
                const wave = params.data?.waves[week.id]
                if (!wave) return undefined
                if (wave.finalQty == null)
                  return params.data?.wpQty != null
                    ? `Planned ${formatUnits(wave.qty)} units (the rounded wave isn't stored for this frozen version)`
                    : `Planned ${formatUnits(wave.qty)} units (not rounded: no DC)`
                const pack = params.data?.line?.cp
                return wave.finalQty === wave.qty
                  ? `Planned ${formatUnits(wave.qty)} units`
                  : `Planned ${formatUnits(wave.qty)} → ships ${formatUnits(wave.finalQty)}: after the DC split, each DC share rounded up to the case pack${pack ? ` of ${formatUnits(pack)}` : ''}`
              },
            },
            {
              ...metric('avgStore', 'Avg Store Units', 132),
              headerTooltip:
                'Planned units per store (before rounding to the case pack)',
              hide: !metrics,
            },
            {
              ...metric('cubicFtPerDc', 'Avg Cubic Ft / DC', 144),
              headerTooltip:
                'Cube of the planned units per DC (before rounding to the case pack)',
              hide: !metrics,
            },
          ],
        }
      }),
      {
        ...numeric('wpQty', 'Total Wave Quantity', 126),
        headerTooltip:
          'Sum of the wave quantities: every DC share rounded up to the case pack, over all waves',
      },
      {
        ...numeric('varianceUnits', 'Var vs AP', 112),
        headerTooltip:
          'Total Wave Quantity minus the AP quantity. Rounding never changes the AP quantity.',
        valueFormatter: params =>
          params.value == null ? '—' : signedUnits(Number(params.value)),
        cellClass: params =>
          `ag-right-aligned-cell wave-number ${Number(params.value) > 0 ? 'text-amber-800' : ''}`,
      },
      {
        ...numeric('variancePercent', 'Var %', 96),
        headerTooltip: 'Variance units / AP quantity',
        valueFormatter: params =>
          params.value == null
            ? '—'
            : `${Number(params.value) > 0 ? '+' : ''}${formatPercent(Number(params.value))}`,
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        headerClass: 'sr-only-header',
        width: mobile ? 44 : 82,
        // On phones the pinned columns would leave no room for the waves.
        pinned: mobile ? null : 'right',
        lockPinned: true,
        suppressKeyboardEvent: ({ event }) => keepNativeKeys(event),
        sortable: false,
        resizable: false,
        cellClass: 'sku-actions-cell',
        cellRenderer: ({ data }: ICellRendererParams<SkuWaveRow>) => {
          if (!data?.line) return null
          const line = data.line
          const count = commentCounts[line.id] ?? 0
          return (
            <div className="flex flex-col items-center justify-center sm:flex-row">
              <Button
                id={`sku-comments-${line.id}`}
                variant="ghost"
                size="icon"
                className="relative h-7 w-8"
                title={`${count} comments`}
                aria-label={`Comments for ${line.sku} ${line.type}`}
                onClick={event => onOpenComments(line, event.currentTarget)}
              >
                <MessageCircle size={15} />
                {count > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-0.5 text-[9px] text-primary-foreground">
                    {count}
                  </span>
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-8"
                disabled={readOnly || !data.adjusted}
                title="Reset row to overall plan"
                aria-label={`Reset row ${line.sku} ${line.type}`}
                onClick={() => onResetRow(line.id)}
              >
                <RotateCcw size={13} />
              </Button>
            </div>
          )
        },
      },
    ]
  }, [
    mobile,
    readOnly,
    canSelect,
    references,
    separateProduct,
    weeks,
    metrics,
    commentCounts,
    onResetRow,
    onOpenComments,
  ])

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
        <div className="relative min-w-44 flex-1 sm:max-w-72">
          <Search
            size={15}
            className="absolute left-3 top-2.5 text-muted-foreground"
          />
          <Input
            aria-label="Search SKU lines"
            placeholder="Search SKU, product or vendor..."
            value={query}
            onChange={event => setQuery(event.target.value)}
            className="h-9 bg-secondary/40 pl-9 text-xs"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={reviewOnly ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={reviewOnly}
            onClick={() => setReviewOnly(!reviewOnly)}
          >
            <AlertTriangle size={14} /> Needs review{' '}
            <span className="text-muted-foreground">
              {rows.filter(row => row.needsReview).length}
            </span>
          </Button>
          <SkuFilterButton filter={filter} onApplyFilter={setFilter} />
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline">
                <Columns3 size={14} /> Columns{' '}
                {(references.length > 0 || separateProduct) &&
                  `(${references.length + (separateProduct ? 1 : 0)})`}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              collisionPadding={8}
              aria-label="Reference columns"
              className="w-80 p-0 flex flex-col max-h-[min(30rem,var(--radix-popover-content-available-height))]"
            >
              <div className="shrink-0 p-3 pb-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search columns..."
                    value={columnQuery}
                    onChange={event => setColumnQuery(event.target.value)}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
              </div>
              <div
                role="checkbox"
                aria-checked={separateProduct}
                tabIndex={0}
                className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-border cursor-pointer select-none"
                onClick={() => setSeparateProduct(value => !value)}
                onKeyDown={event =>
                  handleCheckboxKeyDown(event, () =>
                    setSeparateProduct(value => !value)
                  )
                }
              >
                <span className="pointer-events-none">
                  <Checkbox
                    checked={separateProduct}
                    aria-hidden
                    tabIndex={-1}
                  />
                </span>
                <span className="text-sm">Separate SKU and description</span>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto py-1">
                {filteredRefs.map(column => {
                  const checked = references.includes(column.field)
                  const toggleColumn = () =>
                    setReferences(current =>
                      checked
                        ? current.filter(field => field !== column.field)
                        : [...current, column.field]
                    )
                  return (
                    <div
                      key={column.field}
                      role="checkbox"
                      aria-checked={checked}
                      tabIndex={0}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 cursor-pointer select-none"
                      onClick={toggleColumn}
                      onKeyDown={event =>
                        handleCheckboxKeyDown(event, toggleColumn)
                      }
                    >
                      <span className="pointer-events-none">
                        <Checkbox checked={checked} aria-hidden tabIndex={-1} />
                      </span>
                      <span className="text-sm flex-1">{column.label}</span>
                    </div>
                  )
                })}
                {filteredRefs.length === 0 && (
                  <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                    No columns match your search
                  </div>
                )}
              </div>
              <div className="shrink-0 border-t border-border px-3 py-2 flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="cursor-pointer flex-1 h-7 text-xs"
                  onClick={() => {
                    setReferences(referenceColumns.map(column => column.field))
                    setSeparateProduct(true)
                  }}
                >
                  Show all
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="cursor-pointer flex-1 h-7 text-xs"
                  onClick={() => {
                    setReferences([])
                    setSeparateProduct(false)
                  }}
                >
                  Hide all
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <Button
            size="sm"
            variant={metrics ? 'secondary' : 'outline'}
            aria-pressed={metrics}
            onClick={() => setMetrics(!metrics)}
          >
            <SlidersHorizontal size={14} /> Wave metrics
          </Button>
        </div>
      </div>
      {canSelect && selected.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 border-b bg-white px-4 py-2.5"
          data-testid="sku-bulk-bar"
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onSelectedChange(new Set())}
              className="flex cursor-pointer items-center justify-center"
              aria-label="Clear selection"
            >
              <Minus className="h-4 w-4 text-primary" />
            </button>
            <span className="text-sm font-semibold text-[#1c1c1c]">
              {selected.size} {selected.size === 1 ? 'Item' : 'Items'} Selected
            </span>
            <span className="hidden text-[11px] text-muted-foreground md:inline">
              Edit a selected line's Wave % to change all of them
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onResetSelected}
              className="border-primary text-primary hover:bg-primary/10"
              disabled={readOnly || bulkPending}
            >
              Reset to plan
            </Button>
          </div>
        </div>
      )}
      <div
        className="sku-wave-grid wave-planning-grid ag-theme-michaels w-full"
        aria-label="SKU wave allocations"
      >
        <AgGridReact<SkuWaveRow>
          ref={grid}
          theme="legacy"
          context={selection.current}
          rowData={filtered}
          columnDefs={columns}
          getRowId={params => params.data.id}
          pinnedBottomRowData={totals}
          defaultColDef={{
            sortable: true,
            resizable: true,
            suppressMovable: true,
            cellDataType: false,
            wrapHeaderText: true,
          }}
          rowHeight={mobile ? 68 : 58}
          headerHeight={42}
          groupHeaderHeight={58}
          singleClickEdit
          readOnlyEdit
          stopEditingWhenCellsLoseFocus
          invalidEditValueMode="revert"
          ensureDomOrder
          tooltipShowDelay={450}
          overlayNoRowsTemplate="<span class='text-sm'>No matching lines. Adjust your search or filters.</span>"
          onCellKeyDown={event => {
            if ('column' in event && event.column.getColId() === 'actions')
              focusCellControl(event)
          }}
          onCellEditRequest={event => {
            const week = weeks.find(
              item => `${item.id}-percent` === event.column.getColId()
            )
            const text = String(event.newValue ?? '')
            // Every valid commit goes to the page: what changes depends on the
            // whole result (auto-filled cells, a selection), not just this cell.
            if (week && event.data.line && !validateWavePercent(text))
              onEdit(event.data.id, week.id, text)
          }}
          onFirstDataRendered={event => {
            if (mobile && weeks[0])
              event.api.ensureColumnVisible(`${weeks[0].id}-percent`, 'start')
          }}
          tabToNextCell={params => {
            if (!params.editing) return params.nextCellPosition ?? false
            const current = weeks.findIndex(
              week =>
                `${week.id}-percent` ===
                params.previousCellPosition.column.getColId()
            )
            const direction = params.backwards ? -1 : 1
            let index = current + direction
            let rowIndex = params.previousCellPosition.rowIndex
            if (index < 0 || index >= weeks.length) {
              index = params.backwards ? weeks.length - 1 : 0
              rowIndex += direction
            }
            if (rowIndex < 0 || rowIndex >= params.api.getDisplayedRowCount())
              return false
            const column = params.api.getColumn(`${weeks[index].id}-percent`)
            return column ? { rowIndex, rowPinned: null, column } : false
          }}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-secondary/30 px-3 py-2 text-[11px] text-muted-foreground">
        <span>
          {filtered.length} of {rows.length} lines · {weeks.length} wave dates
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Pencil size={11} /> Wave % is editable · Other values are calculated
          or reference data
        </span>
        {rows.every(row => !row.needsReview) && (
          <span className="inline-flex items-center gap-1">
            <Check size={12} /> All lines allocated
          </span>
        )}
      </div>
    </>
  )
}
